import React, { useState, useEffect } from "react";
import { Table, Input, DatePicker, Modal, Upload, message, Button, Tooltip } from "antd";
import { useGetRecentPatientsMutation, useUploadPatientCsvMutation } from '../../redux/slices/patientSlice';
import { MdEdit } from "react-icons/md";
import { UploadOutlined, InboxOutlined, DownloadOutlined } from '@ant-design/icons';
import { useNavigate } from "react-router-dom";
import { FaExclamationTriangle } from "react-icons/fa";
import dayjs from "dayjs"

const { RangePicker } = DatePicker;
const { Dragger } = Upload;

const PatientList = () => {
   const navigate = useNavigate()
   const [getRecentPatients, { isLoading }] = useGetRecentPatientsMutation();
   const [uploadPatientCsv, { isLoading: isUploading }] = useUploadPatientCsvMutation();
   const [filteredPatientData, setFilteredPatientData] = useState([]);
   const [patientTotal, setPatientTotal] = useState(0);
   const [filteredPatientTotal, setFilteresPatientTotal] = useState(0);
   const [patientPerPage, setPatientPerPage] = useState(10);
   const [patientPage, setPatientPage] = useState(1);
   const [search, setSearch] = useState('');
   const [totalPatients, setTotalPatients] = useState(null);
   const [sortField, setSortField] = useState(null);
   const [sortOrder, setSortOrder] = useState(null);
   const [selectedDateRange, setSelectedDateRange] = useState([
      dayjs().subtract(30, "day"),
      dayjs(),
   ]);
   const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
   const [fileList, setFileList] = useState([]);
   const [getMissingDetails, setGetMissingDetails] = useState(false);
   const [isExporting, setIsExporting] = useState(false);

   // Helper function to check if a field is missing
   const isMissing = (value) => {
      return !value || value === "N/A" || value.trim() === "";
   };

   const hasMissingRequiredFields = (record) => {
      return isMissing(record?.address) ||
         isMissing(record?.city) ||
         isMissing(record?.state) ||
         isMissing(record?.zip);
   };

   // This is a helper function to get list of missing fields
   const getMissingFieldsList = (record) => {
      const missing = [];
      if (isMissing(record?.address)) missing.push('Address');
      if (isMissing(record?.city)) missing.push('City');
      if (isMissing(record?.state)) missing.push('State');
      if (isMissing(record?.zip)) missing.push('Zip');
      if (isMissing(record?.email)) missing.push('Email');
      return missing.join(', ');
   };

   const getPatientData = (value) => {
      getRecentPatients(value).then((response) => {
         setFilteredPatientData(response?.data?.data);
         setPatientTotal(response?.data?.total);
         setFilteresPatientTotal(response?.data?.total);
         setTotalPatients(response?.data?.total_patients);
      }).catch((err) => {
         console.error("err", err);
      });
   }

   useEffect(() => {
      let query = `?per_page=${patientPerPage}&page=${patientPage}`;

      if (search !== '') {
         query += `&search=${search}`
      }
      if (sortField && sortOrder) {
         query += `&sort_by=${sortField}&sort_direction=${sortOrder}`;
      }
      if (selectedDateRange && selectedDateRange?.length === 2) {
         const formattedStartDate = dayjs(selectedDateRange[0]).format("YYYY-MM-DD");
         const formattedEndDate = dayjs(selectedDateRange[1]).format("YYYY-MM-DD");
         query += `&start_date=${formattedStartDate}&end_date=${formattedEndDate}`;
      }
      if (getMissingDetails) {
         query += `&get_missing_details=true`;
      }
      getPatientData(query)
   }, [patientPerPage, patientPage, sortField, sortOrder, search, selectedDateRange, getMissingDetails])

   const onPageSizeChange = (current, size) => {
      setPatientPerPage(size);
      setPatientPage(1);
   };

   const generatePageSizeOptions = (total) => {
      const options = [];
      const maxPageSize = Math.min(total, 100);

      for (let i = 5; i <= maxPageSize; i += 5) {
         options.push(i);
      }
      if (total > 5 && !options.includes(10)) {
         options.push(10);
      }

      if (total > 10 && !options.includes(15)) {
         options.push(15);
      }
      return options.sort((a, b) => a - b);
   };

   const pageSizeOptions = generatePageSizeOptions(patientTotal);

   const handleTableChange = (pagination, filters, sorter) => {
      if (sorter.order) {
         setSortField(sorter.field);
         setSortOrder(sorter.order === "ascend" ? "asc" : "desc");
      } else {
         setSortField(null);
         setSortOrder(null);
      }
   };

   const tableColumns = [
      {
         title: "S.No",
         key: "serial_number",
         render: (text, record, index) =>
            <span>{(patientPage - 1) * patientPerPage + index + 1}</span>,
      },
      {
         title: "Patient Name",
         dataIndex: "first_name",
         key: "first_name",
         sorter: true,
         render: (text, record) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
               <span>{`${record?.first_name} ${record?.last_name}`}</span>
               {hasMissingRequiredFields(record) && (
                  <Tooltip title={`Missing: ${getMissingFieldsList(record)}`}>
                     <FaExclamationTriangle style={{ color: '#faad14', fontSize: '14px' }} />
                  </Tooltip>
               )}
            </div>
         )
      },
      {
         title: "Email",
         dataIndex: "email",
         key: "email",
         sorter: true,
         render: (text, record) => (
            <span>{record?.email || "N/A"}</span>
         )
      },
      {
         title: "Phone",
         dataIndex: "phone",
         key: "phone",
         sorter: true,
         render: (text, record) => (
            <span>{record?.phone || "N/A"}</span>
         )
      },
      {
         title: "Address",
         dataIndex: "address",
         key: "address",
         sorter: true,
         render: (text, record) => (
            <span style={{
               color: isMissing(record?.address) ? '#ff4d4f' : 'inherit',
               fontStyle: isMissing(record?.address) ? 'italic' : 'normal'
            }}>
               {record?.address || "N/A"}
            </span>
         )
      },
      {
         title: "City",
         dataIndex: "city",
         key: "city",
         sorter: true,
         render: (text, record) => (
            <span style={{
               color: isMissing(record?.city) ? '#ff4d4f' : 'inherit',
               fontStyle: isMissing(record?.city) ? 'italic' : 'normal'
            }}>
               {record?.city || "N/A"}
            </span>
         )
      },
      {
         title: "State",
         dataIndex: "state",
         key: "state",
         sorter: true,
         render: (text, record) => (
            <span style={{
               color: isMissing(record?.state) ? '#ff4d4f' : 'inherit',
               fontStyle: isMissing(record?.state) ? 'italic' : 'normal'
            }}>
               {record?.state || "N/A"}
            </span>
         )
      },
      {
         title: "Zip",
         dataIndex: "zip",
         key: "zip",
         sorter: true,
         render: (text, record) => (
            <span style={{
               color: isMissing(record?.zip) ? '#ff4d4f' : 'inherit',
               fontStyle: isMissing(record?.zip) ? 'italic' : 'normal'
            }}>
               {record?.zip || "N/A"}
            </span>
         )
      },
      {
         title: "Action",
         dataIndex: "action",
         key: "action",
         render: (text, record) => (
            <MdEdit style={{ cursor: "pointer", color: '#b4a099' }} onClick={() => navigate("/edit-patient", {
               state: { patientId: record?.id }
            })} />
         )
      },
   ];

   const handlePatientSearch = async (event) => {
      setPatientPage(1)
      setSearch(event?.target?.value);
   };

   const handleOnChangeDate = (dates) => {
      setSelectedDateRange(dates);

      if (dates && dates.length === 2) {
         const formattedStartDate = dayjs(dates[0]).format("YYYY-MM-DD");
         const formattedEndDate = dayjs(dates[1]).format("YYYY-MM-DD");

         let query = `?per_page=${patientPerPage}&page=${patientPage}&start_date=${formattedStartDate}&end_date=${formattedEndDate}`;
         if (sortField && sortOrder) {
            query += `&sort_by=${sortField}&sort_direction=${sortOrder}`;
         }
         if (getMissingDetails) {
            query += `&get_missing_details=true`;
         }
         getPatientData(query);
      } else {
         let query = `?per_page=${patientPerPage}&page=${patientPage}`;
         if (sortField && sortOrder) {
            query += `&sort_by=${sortField}&sort_direction=${sortOrder}`;
         }
         if (getMissingDetails) {
            query += `&get_missing_details=true`;
         }
         getPatientData(query);
      }
   };

   const handleMissingDetailsToggle = () => {
      setPatientPage(1);
      setGetMissingDetails(!getMissingDetails);
   };

   const handleExportMissingPatients = async () => {
      try {
         setIsExporting(true);

         // Fetch all patients with missing details (without pagination)
         let query = `?per_page=1000000&page=1&get_missing_details=true`;

         if (search !== '') {
            query += `&search=${search}`
         }
         if (sortField && sortOrder) {
            query += `&sort_by=${sortField}&sort_direction=${sortOrder}`;
         }
         if (selectedDateRange && selectedDateRange?.length === 2) {
            const formattedStartDate = dayjs(selectedDateRange[0]).format("YYYY-MM-DD");
            const formattedEndDate = dayjs(selectedDateRange[1]).format("YYYY-MM-DD");
            query += `&start_date=${formattedStartDate}&end_date=${formattedEndDate}`;
         }

         const response = await getRecentPatients(query).unwrap();
         const patientsData = response?.data || [];

         if (patientsData.length === 0) {
            message.warning('No patients with missing details to export');
            setIsExporting(false);
            return;
         }

         // Prepare data for export
         const exportData = patientsData.map((patient, index) => ({
            'S.No': index + 1,
            'First Name': patient.first_name || '',
            'Last Name': patient.last_name || '',
            'Email': patient.email || 'N/A',
            'Phone': patient.phone || 'N/A',
            'Address': patient.address || 'N/A',
            'City': patient.city || 'N/A',
            'State': patient.state || 'N/A',
            'Zip': patient.zip || 'N/A',
            'Missing Fields': getMissingFieldsList(patient),
            'Last Visit': patient.last_visit ? dayjs(patient.last_visit).format('MM/DD/YYYY') : 'N/A'
         }));

         // Create CSV content
         const headers = Object.keys(exportData[0]);
         const csvContent = [
            headers.join(','),
            ...exportData.map(row =>
               headers.map(header => {
                  const value = row[header];
                  // Escape commas and quotes in values
                  if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
                     return `"${value.replace(/"/g, '""')}"`;
                  }
                  return value;
               }).join(',')
            )
         ].join('\n');

         // Create blob and download
         const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
         const link = document.createElement('a');
         const url = URL.createObjectURL(blob);

         link.setAttribute('href', url);
         link.setAttribute('download', `patients_missing_details_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.csv`);
         link.style.visibility = 'hidden';

         document.body.appendChild(link);
         link.click();
         document.body.removeChild(link);

         message.success(`Successfully exported ${patientsData.length} patients with missing details`);
      } catch (error) {
         console.error('Export error:', error);
         message.error('Failed to export patients. Please try again.');
      } finally {
         setIsExporting(false);
      }
   };

   const showUploadModal = () => {
      setIsUploadModalVisible(true);
   };

   const handleUploadCancel = () => {
      setIsUploadModalVisible(false);
      setFileList([]);
   };

   const uploadProps = {
      name: 'file',
      multiple: false,
      fileList: fileList,
      accept: '.csv,.xlsx',
      beforeUpload: (file) => {
         const isCSV = file.type === 'text/csv' || file.name.endsWith('.csv') || file.name.endsWith('.xlsx');
         if (!isCSV) {
            message.error('You can only upload CSV or XLSX files!');
            return Upload.LIST_IGNORE;
         }
         setFileList([file]);
         return false;
      },

      showUploadList: {
         showRemoveIcon: false,
      },
   };

   const handleUpload = async () => {
      if (fileList.length === 0) {
         message.error('Please select a file to upload!');
         return;
      }

      const formData = new FormData();
      formData.append('file', fileList[0]);

      try {
         const response = await uploadPatientCsv(formData).unwrap();

         message.success(response?.message || 'File uploaded successfully!');
         setIsUploadModalVisible(false);
         setFileList([]);

         let query = `?per_page=${patientPerPage}&page=${patientPage}`;
         if (search !== '') {
            query += `&search=${search}`
         }
         if (sortField && sortOrder) {
            query += `&sort_by=${sortField}&sort_direction=${sortOrder}`;
         }
         if (selectedDateRange && selectedDateRange?.length === 2) {
            const formattedStartDate = dayjs(selectedDateRange[0]).format("YYYY-MM-DD");
            const formattedEndDate = dayjs(selectedDateRange[1]).format("YYYY-MM-DD");
            query += `&start_date=${formattedStartDate}&end_date=${formattedEndDate}`;
         }
         if (getMissingDetails) {
            query += `&get_missing_details=true`;
         }
         getPatientData(query);

      } catch (error) {
         console.error('Upload error:', error);
         message.error(error?.data?.message || 'Upload failed. Please try again.');
      }
   };

   return (
      <div className="wrapper">
         <div className="container-fluid">
            <div className="row mb-5">
               <div className="col-md-12">
                  <div className="titleBar titleRow">
                     <h4>Patient List</h4>
                     <div className="d-flex align-items-end gap-3">
                        {/* Missing Details Toggle Button */}
                        <div className="filter-button-wrapper">
                           <label className="dynamicLabel">Filter Patients</label>
                           <Button
                              icon={<FaExclamationTriangle style={{ fontSize: '16px' }} />}
                              onClick={handleMissingDetailsToggle}
                              size="large"
                              className={`missing-details-toggle ${getMissingDetails ? 'active' : ''}`}
                           >
                              {getMissingDetails ? 'Show All Patients' : 'Missing Details'}
                           </Button>
                        </div>

                        {/* Export Button - Only visible when filter is active */}
                        {getMissingDetails && (
                           <div className="filter-button-wrapper">
                              <label className="dynamicLabel">Export Data</label>
                              <Button
                                 icon={<DownloadOutlined style={{ fontSize: '16px' }} />}
                                 onClick={handleExportMissingPatients}
                                 size="large"
                                 loading={isExporting}
                                 className="export-button"
                              >
                                 Export
                              </Button>
                           </div>
                        )}

                        {/* Upload Button */}
                        <div className="filter-button-wrapper">
                           <label className="dynamicLabel">Import Data</label>
                           <Button
                              icon={<UploadOutlined style={{ fontSize: '16px' }} />}
                              onClick={showUploadModal}
                              size="large"
                              className="upload-button"
                           >
                              Upload File
                           </Button>
                        </div>

                        {/* Date Range Picker */}
                        <div className="dateRangeWrapper">
                           <label htmlFor="lastVisitSelect" className="dynamicLabel">Last Visited</label>
                           <RangePicker
                              format="MM/DD/YYYY"
                              className="formSelect w-100"
                              allowClear
                              size="large"
                              value={selectedDateRange}
                              ranges={{
                                 Today: [dayjs(), dayjs()],
                                 "This Week": [dayjs().startOf("week"), dayjs().endOf("week")],
                                 "This Month": [dayjs().startOf("month"), dayjs().endOf("month")],
                                 "Last Month": [
                                    dayjs().subtract(1, "month").startOf("month"),
                                    dayjs().subtract(1, "month").endOf("month"),
                                 ],
                                 "Last 6 Months": [
                                    dayjs().subtract(6, "month").startOf("month"),
                                    dayjs().endOf("month"),
                                 ],
                                 "Last 30 Days": [dayjs().subtract(30, "day"), dayjs()],
                              }}
                              onChange={(dates) => handleOnChangeDate(dates)}
                           />
                        </div>

                        {/* Search Input */}
                        <div className='inputContainer' style={{ marginBottom: '0px' }}>
                           <label htmlFor="lastVisitSelect" className="dynamicLabel">Search Here</label>
                           <Input
                              size="large"
                              placeholder="Search patients..."
                              onChange={handlePatientSearch}
                              className="homesearchInput titleRowInput"
                              allowClear
                              prefix={<span style={{ color: '#8b7d6b', marginRight: '8px' }}>🔍</span>}
                           />
                        </div>
                     </div>
                  </div>
               </div>
               <div className="col-md-12">
                  <div className="tableWrapper customPaginationTable">
                     <Table
                        className="registered-user-table"
                        columns={tableColumns}
                        dataSource={isLoading ? [] : filteredPatientData}
                        pagination={{
                           current: patientPage,
                           pageSize: patientPerPage,
                           total: filteredPatientTotal,
                           onChange: (page) => setPatientPage(page),
                           onShowSizeChange: onPageSizeChange,
                           showSizeChanger: true,
                           showQuickJumper: true,
                           pageSizeOptions: pageSizeOptions,
                        }}
                        loading={isLoading}
                        onChange={handleTableChange}
                        showSorterTooltip={false}
                        scroll={{ x: 'auto' }}
                        bordered
                        locale={{
                           emptyText: isLoading ? ' ' : 'No Data'
                        }}
                        rowClassName={(record) =>
                           hasMissingRequiredFields(record) ? 'row-missing-data' : ''
                        }
                        footer={() =>
                           <>
                              <span>
                                 <b>Total Patients:</b> {search !== "" ? filteredPatientTotal : totalPatients}
                                 {getMissingDetails && (
                                    <span style={{ marginLeft: '1rem', color: '#ff4d4f' }}>
                                       (Showing patients with missing details)
                                    </span>
                                 )}
                              </span>
                           </>
                        }
                     />
                  </div>
               </div>
            </div>
         </div>

         <Modal
            title="Upload Patient Data"
            open={isUploadModalVisible}
            onCancel={handleUploadCancel}
            onOk={handleUpload}
            okText="Upload"
            confirmLoading={isUploading}
            okButtonProps={{ disabled: fileList.length === 0 }}
         >
            <Dragger {...uploadProps}>
               <p className="ant-upload-drag-icon">
                  <InboxOutlined />
               </p>
               <p className="ant-upload-text">Click or drag a XLSX file to this area to upload</p>
               <p className="ant-upload-hint">
                  Support for a single XLSX file upload. Please ensure the file format matches the patient data structure.
               </p>
            </Dragger>
         </Modal>
         <style jsx>{`
   .filter-button-wrapper {
      display: flex;
      flex-direction: column;
   }

   .missing-details-toggle {
      height: 50px;
      min-width: 180px;
      background: white;
      border: 2px solid #e8e8e8 !important;
      color: #595959 ;
      font-weight: 500;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      display: flex;
      align-items: center;
      gap: 8px;
      border-radius: 8px;
   }

   .missing-details-toggle:hover {
      border-color: #b4a099 !important;
      color: #b4a099 !important;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(250, 173, 20, 0.15);
   }

   .missing-details-toggle.active {
      background: linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%) !important;
      border-color: #ff4d4f !important;
      color: white !important;
      box-shadow: 0 4px 12px rgba(255, 77, 79, 0.25);
   }

   .missing-details-toggle.active:hover {
      background: linear-gradient(135deg, #ff7875 0%, #ff4d4f 100%);
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(255, 77, 79, 0.35);
   }

   .export-button {
      height: 50px;
      min-width: 120px;
      background: linear-gradient(135deg, #52c41a 0%, #73d13d 100%);
      border: none;
      color: white;
      font-weight: 500;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      display: flex;
      align-items: center;
      gap: 8px;
      border-radius: 8px;
   }

   .export-button:hover {
      background: linear-gradient(135deg, #73d13d 0%, #52c41a 100%) !important;
      transform: translateY(-2px);
      color: white !important;
   }

   .export-button:disabled {
      background: #d9d9d9;
      transform: none;
      box-shadow: none;
   }

   .upload-button {
      height: 50px;
      min-width: 150px;
      background: linear-gradient(135deg, #b4a099 0%, #9d8d82 100%);
      border: none;
      color: white;
      font-weight: 500;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      display: flex;
      align-items: center;
      gap: 8px;
      border-radius: 8px;
      box-shadow: 0 2px 8px rgba(180, 160, 153, 0.25);
   }

   .upload-button:hover {
      background: linear-gradient(135deg, #9d8d82 0%, #b4a099 100%) !important;
      transform: translateY(-2px);
      color: white !important;
   }

   .row-missing-data {
      background-color: #fff7f0 !important;
      border-left: 3px solid #faad14 !important;
   }

   .row-missing-data:hover {
      background-color: #ffe7d9 !important;
   }

   .homesearchInput {
      border-radius: 8px;
      border: 2px solid #e8e8e8;
      transition: all 0.3s ease;
      height: 3.3rem
   }

   .homesearchInput:hover,
   .homesearchInput:focus {
      border-color: #b4a099;
      box-shadow: 0 0 0 2px rgba(180, 160, 153, 0.1);
   }

   .dateRangeWrapper .ant-picker {
      border-radius: 8px;
      border: 2px solid #e8e8e8;
      transition: all 0.3s ease;
   }

   .dateRangeWrapper .ant-picker:hover,
   .dateRangeWrapper .ant-picker-focused {
      border-color: #b4a099;
      box-shadow: 0 0 0 2px rgba(180, 160, 153, 0.1);
   }

   .dynamicLabel {
      font-size: 13px;
      font-weight: 600;
      color: #595959;
      margin-bottom: 8px;
      display: block;
   }

   .titleBar {
      padding: 24px 0;
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      flex-wrap: wrap;
      gap: 16px;
   }

   .titleBar h4 {
      font-size: 24px;
      font-weight: 600;
      color: #262626;
      margin: 0;
   }

   @media (max-width: 1200px) {
      .titleBar {
         flex-direction: column;
         align-items: flex-start;
      }

      .titleBar > div {
         width: 100%;
      }

      .d-flex.gap-3 {
         flex-wrap: wrap;
      }

      .filter-button-wrapper,
      .dateRangeWrapper,
      .inputContainer {
         flex: 1 1 auto;
         min-width: 200px;
      }
   }
`}</style>
      </div>
   )
}

export default PatientList;